import * as React from 'react';
import { Overlay } from '../components/Overlay';
import { OverlayOption, OverlayOptions } from '../components/OverlayOptions';
import { storeDispatch, isUserPro, getStoreState } from '../state/Store';
import * as Actions from '../state/actions';
import RecordType from '../data/RecordType';
import { openURLFromLite, pause } from '../utils';
import { trackEvent } from 'utils/Tracker';
import styled from 'styled-components';
import { OverlayIcon } from 'components/OverlayIcon';
import watermark from '../components/overlay-icons/watermark.svg';
import goPro from '../components/overlay-icons/go-pro.svg';
import { PlatformType } from 'data/PlatformType';
import { useMobileLayout } from 'hooks/useMobileLayout';
import { hoverMixin } from 'utils/style';
import { isFigmaPro } from 'state/AppState';
// const Switch = require('rc-switch').default;

// const LOCAL_STORAGE_USE_ASYNC_KEY = 'USE_ASYNC_RECORDING';

const PromoBox = styled.div`
  padding: 20px;
  border: 1px solid var(--accent-color);
  border-radius: 6px;
  background-color: var(--accent-color-alpha-10);
  cursor: pointer;
  width: 100%;

  > * {
    pointer-events: none;
  }

  ${hoverMixin('background-color: var(--accent-color-alpha-30);')}
`;

enum ExportType {
  Image = 'image',
  Gif = 'gif',
  Video = 'video'
}

export const Export: React.FunctionComponent = () => {
  const { app } = getStoreState();
  const { isBeatflyerLite, platform } = app;
  // const [forceRealtimeRecording, setForceRealtimeRecording] = React.useState(
  //   getValueFromLocalStorage(LOCAL_STORAGE_USE_ASYNC_KEY) !== 'false'
  // );
  const isMobileApp = platform === PlatformType.iOSApp;
  const isMobile = useMobileLayout();

  const didSelectOption = (key: ExportType) => {
    switch (key) {
      case ExportType.Image:
        storeDispatch(Actions.exportImage());
        break;
      case ExportType.Gif:
        storeDispatch(Actions.startRecording(RecordType.GIF));
        break;
      case ExportType.Video:
        storeDispatch(
          Actions.startRecording(
            RecordType.Video,
            isMobileApp ? { forceRealtimeRecording: false } : null
          )
        );
        break;
      default:
        break;
    }
  };

  const stillImage: OverlayOption = {
    key: ExportType.Image,
    title: 'Still image',
    description: (
      <>
        {isMobile ? (
          'Share a non-compressed still picture.'
        ) : (
          <>
            Share a non-compressed still picture. Ideal for prints.
            <br />
            &nbsp;
          </>
        )}
      </>
    ),
    cta: 'Export image'
  };

  const animatedGif: OverlayOption = {
    key: ExportType.Gif,
    title: 'short Animated gif',
    description:
      'Share a short animation loop of your flyer without sound. Ideal for newletters and websites.',
    cta: 'Export GIF',
    footer: (
      <a
        onClick={e => {
          e.stopPropagation();
          storeDispatch(
            Actions.startRecording(RecordType.GIF, {
              transparentBackground: true
            })
          );
        }}
      >
        Export with transparent background
      </a>
    )
  };

  const video: OverlayOption = {
    key: ExportType.Video,
    title: 'full video (MP4)',
    description: (
      <>
        {isMobile ? (
          <>Share your whole flyer with sound.</>
        ) : (
          <>
            Share your whole flyer with sound. Ideal for social media.
            <br />
            &nbsp;
          </>
        )}
      </>
    ),
    cta: 'Export video'
    // footer: isMobileApp ? (
    //   <>
    //     Don't skip frames (slow but reliable):{' '}
    //     <Switch
    //       checked={!forceRealtimeRecording}
    //       onChange={(value: boolean) => {
    //         setValueInLocalStorage(
    //           LOCAL_STORAGE_USE_ASYNC_KEY,
    //           value ? 'false' : 'true'
    //         );
    //         setForceRealtimeRecording(!value);
    //       }}
    //     />
    //   </>
    // ) : undefined
  };

  const items: OverlayOption[] = [video];
  if (!isMobileApp) {
    items.push(animatedGif);
  }
  if (!isBeatflyerLite) {
    items.push(stillImage);
  }

  const upgrade = () => {
    trackEvent('Clicked Upgrade', { location: 'Export' });
    storeDispatch(Actions.closeModal(true));
    pause(1);
    storeDispatch(Actions.showBuyingOptions());
  };

  const tryBeatflyerPro = () => {
    openURLFromLite('https://editor.beatflyer.com');
    trackEvent('clickedGoPro', { location: 'export' });
  };

  const buyFigma = () => {
    storeDispatch(Actions.showBuyFigma('export'));
  };

  const isFigma = platform === PlatformType.Figma;

  return (
    <Overlay title="Export">
      <OverlayOptions items={items} onSelect={didSelectOption} />
      {isBeatflyerLite && !isFigmaPro(app) && (
        <PromoBox
          data-testid="promo-box"
          onClick={isFigma ? buyFigma : tryBeatflyerPro}
          style={{ width: 636 }}
        >
          <OverlayIcon imageUrl={goPro} background="var(--accent-color)" />
          <h1>Use sound-reactive effects and more</h1>
          <p
            style={{
              marginTop: 10,
              marginBottom: 20,
              fontSize: 15,
              lineHeight: '20px'
            }}
          >
            {isFigma ? (
              <>
                For more features like better rendering, music syncronization,
                and audio visualizers.
              </>
            ) : (
              <>
                For more features like better rendering, ready-made teamplates,
                <br />
                music syncronization, and audio visualizers.
              </>
            )}
          </p>
          <button
            className="large primary"
            onClick={isFigma ? buyFigma : tryBeatflyerPro}
          >
            {isFigma ? <>Upgrade</> : <>Try Beatflyer Pro</>}
          </button>
        </PromoBox>
      )}
      {!isUserPro() && !isBeatflyerLite && (
        <PromoBox data-testid="promo-box" onClick={upgrade}>
          <OverlayIcon imageUrl={watermark} background="var(--accent-color)" />
          <h1>
            {isMobile ? <>Remove our logo</> : <>Want to remove our logo?</>}
          </h1>
          <p
            style={{
              marginTop: 10,
              marginBottom: 20,
              fontSize: 15,
              lineHeight: '20px'
            }}
          >
            {isMobile ? (
              <>
                Buy a day pass or subscribe to a plan to remove our branding
                from the videos.
              </>
            ) : (
              <>
                Buy a day pass or subscribe to a plan to remove our branding
                from the videos. With the subscriptions you can also save
                projects for later use.
              </>
            )}
          </p>
          <button className="large primary" onClick={upgrade}>
            Upgrade
          </button>
        </PromoBox>
      )}
    </Overlay>
  );
};



// WEBPACK FOOTER //
// ./src/export/Export.tsx